import { useReduxState } from 'hooks';
import {
  Area,
  AreaChart as RechartsAreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';

import type { BaseAxisProps } from 'recharts/types/util/types';

type AreaProps =
  | {
      lineColors?: string;
      yAxisDataKey: string;
      yAxisDataKeysVisible?: never;
    }
  | {
      lineColors: string[];
      yAxisDataKey: string[];
      yAxisDataKeysVisible?: string[];
    };

type CommonProps = {
  accessibilityLayer?: boolean;
  data: any;
  height?: number;
  stacked?: boolean;
  xAxisDataKey: string;
  yAxisDomain?: BaseAxisProps['domain'];
};

const AreaChart = ({
  accessibilityLayer = false,
  data,
  height = 300,
  lineColors,
  stacked = false,
  xAxisDataKey,
  yAxisDataKey,
  yAxisDataKeysVisible,
  yAxisDomain
}: CommonProps & AreaProps) => {
  const { theme } = useReduxState();

  const yAxis = Array.isArray(yAxisDataKey) ? yAxisDataKey : [yAxisDataKey];

  const lineColor = Array.isArray(lineColors)
    ? lineColors
    : [lineColors || theme.chartMainColor];

  return (
    <div className="line-chart" style={{ height, width: '100%' }}>
      <ResponsiveContainer>
        <RechartsAreaChart
          accessibilityLayer={accessibilityLayer}
          data={data}
          margin={{ top: 20, right: 10, left: 10, bottom: 10 }}
        >
          <defs>
            {yAxis.map((yaxis, i) => (
              <linearGradient
                key={yaxis}
                id={`color-${i}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={lineColor[i]} stopOpacity={0.3} />
                <stop offset="95%" stopColor={lineColor[i]} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>

          <CartesianGrid
            stroke={theme.lightGreyDarkActive}
            strokeDasharray="5 7"
            vertical={false}
          />

          <Tooltip
            animationDuration={200}
            contentStyle={{
              border: 0,
              borderRadius: 10,
              boxShadow: '0 1px 5px 0 rgba(0, 0, 0, 0.2)'
            }}
            itemStyle={{ color: theme.textColor }}
            labelStyle={{ color: theme.textColorLighter }}
            wrapperStyle={{
              border: 0,
              borderRadius: 5,
              fontSize: '1.4rem',
              fontWeight: 600
            }}
          />

          <XAxis
            axisLine={false}
            dataKey={xAxisDataKey}
            dy={10}
            fontSize={14}
            fontWeight={600}
            stroke={theme.textColorLighter}
            tickLine={false}
          />

          <YAxis
            allowDecimals={false}
            axisLine={false}
            domain={yAxisDomain}
            dx={-10}
            fontSize={14}
            fontWeight={600}
            stroke={theme.textColorLighter}
            tickLine={false}
            width="auto"
          />

          {yAxis.map((yaxis, i) => (
            <Area
              activeDot={{
                fill: 'white',
                r: 4,
                stroke: lineColor[i],
                strokeWidth: 2
              }}
              dataKey={yaxis}
              fillOpacity={1}
              fill={`url(#color-${i})`}
              hide={
                yAxisDataKeysVisible && !yAxisDataKeysVisible.includes(yaxis)
              }
              key={yaxis}
              stroke={lineColor[i]}
              strokeWidth={2}
              type="monotone"
              {...(stacked ? { stackId: 'stacked' } : {})}
            />
          ))}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AreaChart;
